import React, { useMemo, useState } from "react";
import { BookMarked, GraduationCap, Landmark, Newspaper, ExternalLink } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { REFERENCES } from "../data/site";

const GROUP_META = {
    "Academic sources": {
        icon: GraduationCap,
        note: "Peer-reviewed studies and academic books on biometric governance, accuracy and surveillance culture.",
    },
    "Journalism & media": {
        icon: Newspaper,
        note: "Reputable reporting used as a secondary source where official documents were unavailable.",
    },
    "Official reports & inquiries": {
        icon: BookMarked,
        note: "Primary documents from the IPCA and the Office of the Privacy Commissioner, plus the Māori Data Sovereignty Network charter.",
    },
    "Legislation & case law": {
        icon: Landmark,
        note: "Statutes and judicial decisions that govern — or fail to govern — biometric surveillance.",
    },
};

const ORDER = [
    "Academic sources",
    "Official reports & inquiries",
    "Legislation & case law",
    "Journalism & media",
];

function renderApa(apa) {
    const parts = apa.split(/<i>|<\/i>/);
    return parts.map((part, i) => (i % 2 === 1 ? <i key={i}>{part}</i> : <span key={i}>{part}</span>));
}

export default function ReferencesPage() {
    const [query, setQuery] = useState("");

    const groups = useMemo(() => {
        const q = query.trim().toLowerCase();
        return ORDER.map((g) => ({
            name: g,
            refs: REFERENCES.filter((r) => r.group === g && (!q || r.apa.toLowerCase().includes(q))),
        })).filter((g) => g.refs.length > 0);
    }, [query]);

    const total = groups.reduce((acc, g) => acc + g.refs.length, 0);

    return (
        <div>
            <PageHero
                fileNo="FR-NZ-10"
                lines={["References", <>& <i className="text-amber">sources</i></>]}
                lede="Every claim in this dossier traces to the sources below, formatted in APA style (7th edition). Academic and official sources form the evidence base; journalism is used only as a secondary record of events."
                meta={[`RECORDS: ${REFERENCES.length}`]}
            />

            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
                    <Reveal className="mb-10 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber mb-3">
                                Source library
                            </p>
                            <h2 className="font-display text-3xl sm:text-5xl text-paper">
                                {total} {total === 1 ? "record" : "records"}, APA 7
                            </h2>
                        </div>
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Filter references…"
                            data-testid="search-reference-input"
                            className="w-full sm:w-80 bg-sheet border border-white/15 px-4 py-3 font-mono text-sm text-paper placeholder:text-ash/50 focus:border-amber focus:outline-none transition-colors"
                        />
                    </Reveal>

                    <div className="space-y-14">
                        {groups.map((group) => {
                            const meta = GROUP_META[group.name];
                            const Icon = meta.icon;
                            return (
                                <Reveal key={group.name}>
                                    <section data-testid={`reference-group-${group.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                        <div className="flex items-baseline justify-between gap-4 border-b border-white/10 pb-4 mb-6">
                                            <div>
                                                <h3 className="font-display text-2xl text-paper flex items-center gap-3">
                                                    <Icon size={20} className="text-amber" />
                                                    {group.name}
                                                </h3>
                                                <p className="text-xs text-ash mt-2 leading-relaxed max-w-2xl">{meta.note}</p>
                                            </div>
                                            <span className="font-mono text-xs text-ash shrink-0">
                                                [{String(group.refs.length).padStart(2, "0")}]
                                            </span>
                                        </div>
                                        <ol className="space-y-5">
                                            {group.refs.map((r) => (
                                                <li
                                                    key={r.id}
                                                    data-testid={`reference-item-${r.id}`}
                                                    className="hanging-ref text-sm sm:text-[15px] leading-relaxed text-ash"
                                                >
                                                    {renderApa(r.apa)}
                                                </li>
                                            ))}
                                        </ol>
                                    </section>
                                </Reveal>
                            );
                        })}
                        {total === 0 && (
                            <p className="text-ash text-sm" data-testid="references-empty-state">
                                No references match “{query}”. Clear the filter to see the full library.
                            </p>
                        )}
                    </div>

                    <Reveal delay={0.1} className="mt-16">
                        <div className="border border-amber/30 bg-amber/[0.05] p-7">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber mb-3">
                                Referencing note
                            </p>
                            <p className="text-sm leading-relaxed text-paper/85">
                                Sources are formatted in APA style, 7th edition, as required by COMP501. Where a URL
                                is shown, the source was available online at the time of writing; official inquiry
                                documents (IPCA, OPC) remain accessible via the organisations' websites. Individual
                                authors' names have been omitted from news sources published organisationally (e.g.
                                Radio New Zealand) in line with APA guidance for group authors.
                            </p>
                            <p className="mt-4 flex items-center gap-2 font-mono text-[11px] text-ash">
                                <ExternalLink size={12} className="text-amber" /> Compiled by Moelili'a (Researcher),
                                reviewed by Stacy (Editor).
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
